import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { getJoinedGreenUsers, getProjectTickets } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import TicketCard from './TicketCard';
import TicketActions from './TicketActions';
import RaiseTicketDialog from './RaiseTicketDialog';

// Tickets at a project's facility: open ones with assign / schedule / resolve, resolved ones folded away
export default function TicketsDialog({ project, canManage, currentUserId, focusTicketId, onClose, onChanged }) {
    const dispatch = useDispatch();
    const fullScreen = useMediaQuery('(max-width:800px)');
    const [tickets, setTickets] = useState([]);
    const [greenUsers, setGreenUsers] = useState([]);
    const [state, setState] = useState({ loading: true, error: null });
    const [notice, setNotice] = useState(null);
    const [raising, setRaising] = useState(false);

    const load = useCallback(() => {
        setState(s => ({ ...s, loading: true }));
        // A ticket visit opened from the calendar may not belong to a project; then list by facility
        dispatch(getProjectTickets(project.id ? { project_id: project.id } : { facility_id: project.facility_id })).then((res) => {
            if (res?.data?.success) { setTickets(res.data.data || []); setState({ loading: false, error: null }); }
            else setState({ loading: false, error: res?.data?.message || 'Could not load tickets' });
        });
    }, [dispatch, project.id, project.facility_id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        dispatch(getJoinedGreenUsers()).then((res) => {
            const users = res?.data?.success ? (res.data.allData || res.data.data || []) : [];
            setGreenUsers(users.filter(u => (u.user_id || '').toLowerCase().includes('@greenvironment')));
        });
    }, [dispatch]);

    const done = (message) => { setNotice(message); load(); onChanged?.(); };
    const open = tickets.filter(t => t.stage !== 'resolved');
    const resolved = tickets.filter(t => t.stage === 'resolved');

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4, bgcolor: '#f6f8f9' } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e9ee' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700 }}>🎫 Tickets · {open.length} open</Typography>
                        <Typography noWrap sx={{ fontSize: '12.5px', color: '#6b7785' }}>{project.facility_name}</Typography>
                    </Box>
                    <Button variant="outlined" size="small" onClick={() => setRaising(true)} sx={buttonStyles.outlined}>＋ Raise for client</Button>
                    <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
                </Box>
                <DialogContent>
                    {notice && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setNotice(null)}>{notice}</Alert>}
                    {state.error && <Alert severity="error" sx={{ mb: 1.5 }}>{state.error}</Alert>}
                    {state.loading && !tickets.length ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress sx={{ color: THEME_COLOR }} /></Box> : (
                        <>
                            {!open.length && <Typography sx={{ textAlign: 'center', color: '#6b7785', py: 2 }}>No open tickets at this facility.</Typography>}
                            {open.map(t => (
                                <TicketCard key={t.id} ticket={t} highlight={t.id === focusTicketId}>
                                    <TicketActions key={`${t.id}-${t.stage}-${t.scheduled_date}`} ticket={t} canManage={canManage} currentUserId={currentUserId} greenUsers={greenUsers} onDone={done} />
                                </TicketCard>
                            ))}
                            {resolved.length > 0 && (
                                <Accordion disableGutters sx={{ mt: 1.5, border: '1px solid #e5e9ee', boxShadow: 'none', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontSize: '13.5px', fontWeight: 500 }}>Resolved · {resolved.length}</Typography></AccordionSummary>
                                    <AccordionDetails>{resolved.map(t => <TicketCard key={t.id} ticket={t} />)}</AccordionDetails>
                                </Accordion>
                            )}
                            <Typography sx={{ fontSize: '12px', color: '#6b7785', mt: 1.5 }}>
                                Tickets from Zoho Desk are read-only here (the Zoho sync would overwrite changes). Client and mobile-app tickets can be assigned, scheduled and resolved.
                                A scheduled visit appears on the calendars under its engineer, who checks in from there.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                {raising && (
                    <RaiseTicketDialog facilityId={project.facility_id} projectId={project.id} onClose={() => setRaising(false)}
                        onRaised={(t, message) => { setRaising(false); done(message); }} />
                )}
            </LocalizationProvider>
        </Dialog>
    );
}
